import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useHubContext } from '@atlassian/company-hub-common';
import {
	type HubBreadcrumbItem,
	useRegisterHubBreadcrumbs,
} from '@atlassian/company-hub-common/hub-breadcrumbs-context';
import {
	HUB_FEATURES,
	useIsHubFeatureEnabled,
} from '@atlassian/company-hub-common/use-is-hub-feature-enabled';

import messages from './messages';

type HubPageBreadcrumbsProps =
	| {
			pageName: string | null | undefined;
			pagePath?: string;
			pageType: 'custom';
			parentFolderName?: string | null;
	  }
	| {
			pageName: string | null | undefined;
			pagePath: string;
			pageType: 'portal';
	  };

const getHubBreadcrumbHref = (relativeBaseUrl: string, routePath: string): string => {
	const basePath = relativeBaseUrl.replace(/\/+$/, '');
	if (/^https?:\/\//.test(routePath)) {
		return routePath;
	}
	if (basePath && (routePath === basePath || routePath.startsWith(`${basePath}/`))) {
		return routePath;
	}
	const path = routePath === '/' ? '' : `/${routePath.replace(/^\/+/, '')}`;

	return `${basePath}${path}` || '/';
};

export const HubPageBreadcrumbs = (props: HubPageBreadcrumbsProps): null => {
	const { pageName, pageType } = props;
	const pagePath = props.pagePath;
	const parentFolderName = pageType === 'custom' ? props.parentFolderName : undefined;
	const { formatMessage } = useIntl();
	const {
		env: { relativeBaseUrl },
	} = useHubContext();
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);
	const items = useMemo<readonly HubBreadcrumbItem[]>(() => {
		const normalizedPageName = pageName?.trim();
		if (!isBreadcrumbsEnabled || !normalizedPageName) {
			return [];
		}

		const homeItem: HubBreadcrumbItem = {
			href: getHubBreadcrumbHref(relativeBaseUrl, '/'),
			id: 'home',
			text: formatMessage(messages.homeBreadcrumb),
		};

		if (pageType === 'portal') {
			if (!pagePath) {
				return [];
			}

			return [
				homeItem,
				{
					href: getHubBreadcrumbHref(relativeBaseUrl, pagePath),
					id: 'portal',
					text: normalizedPageName,
				},
			];
		}

		const normalizedParentFolderName = parentFolderName?.trim();

		return [
			homeItem,
			...(normalizedParentFolderName
				? [
						{
							id: 'parent-folder',
							text: normalizedParentFolderName,
						},
					]
				: []),
			{
				href: pagePath ? getHubBreadcrumbHref(relativeBaseUrl, pagePath) : undefined,
				id: 'custom-page',
				text: normalizedPageName,
			},
		];
	}, [
		formatMessage,
		isBreadcrumbsEnabled,
		pageName,
		pagePath,
		pageType,
		parentFolderName,
		relativeBaseUrl,
	]);

	useRegisterHubBreadcrumbs(items);

	return null;
};
